import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, Subject, of } from 'rxjs';
import { catchError, tap, debounceTime, map, finalize } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { WebSocketService } from './websocket.service';
import { AuthService } from './auth.service';

export interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  receiver_id: string;
  message_text: string;
  is_read: boolean;
  read_at?: string;
  message_type: string;
  metadata?: any;
  created_at: string;
  updated_at: string;
  sender_name: string;
  sender_first_name: string;
  isPending?: boolean;
  tempId?: string;
  sendFailed?: boolean;
}

export interface Conversation {
  id: string;
  listing_id: string;
  listing_table_name: string;
  buyer_id: string;
  seller_id: string;
  last_message_at: string;
  last_message_preview?: string;
  unread_count_buyer: number;
  unread_count_seller: number;
  is_deleted_buyer: boolean;
  is_deleted_seller: boolean;
  created_at: string;
  updated_at: string;
  other_user_name: string;
  other_user_first_name: string;
  other_user_id: string;
  unread_count: number;
  listing_title?: string;
  listing_price?: number;
  listing_status?: string;
  listing?: ListingInfo;
}

export interface ListingInfo {
  id: string;
  title: string;
  price: number;
  currency: string;
  images: string[];
  status: string;
  subcategory?: string;
}

export interface ConversationResponse {
  status: string;
  conversation: Conversation;
}

export interface MessagesResponse {
  status: string;
  messages: Message[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface ConversationsResponse {
  status: string;
  conversations: Conversation[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface UnreadCountResponse {
  status: string;
  unread_count: number;
}

export interface CreateConversationRequest {
  listingId: string;
  listingTableName: string;
  sellerId: string;
}

export interface SendMessageRequest {
  message: string;
  receiverId: string;
}

interface QueuedMessage {
  conversationId: string;
  request: SendMessageRequest;
  tempId: string;
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
}

@Injectable({
  providedIn: 'root',
})
export class MessagesService {
  private apiUrl = environment.apiUrl;

  private unreadCountSubject = new BehaviorSubject<number>(0);
  public unreadCount$ = this.unreadCountSubject.asObservable();

  private conversationsSubject = new BehaviorSubject<Conversation[]>([]);
  public conversations$ = this.conversationsSubject.asObservable();

  private messagesSubject = new BehaviorSubject<{
    [conversationId: string]: Message[];
  }>({});
  public messages$ = this.messagesSubject.asObservable();

  private onlineUsersSubject = new BehaviorSubject<Set<string>>(new Set());
  public onlineUsers$ = this.onlineUsersSubject.asObservable();

  private activeConversations = new Set<string>();
  private messageQueue: QueuedMessage[] = [];
  private isProcessingQueue = false;
  private processedMessageIds = new Set<string>();

  private unreadCountDebouncer = new Subject<void>();

  constructor(
    private http: HttpClient,
    private webSocketService: WebSocketService,
    private authService: AuthService
  ) {
    this.initializeWebSocketListeners();
    this.initializeReconnectionHandler();
    this.initializeUnreadCountDebouncer();
    this.initializeAuthListener();
  }

  private initializeAuthListener(): void {
    this.authService.isLoggedIn$.subscribe((isLoggedIn) => {
      if (isLoggedIn) {
        this.loadUnreadCount();
      } else {
        this.resetState();
      }
    });
  }

  private resetState(): void {
    this.unreadCountSubject.next(0);
    this.messagesSubject.next({});
    this.conversationsSubject.next([]);
    this.onlineUsersSubject.next(new Set());
    this.activeConversations.clear();
    this.messageQueue = [];
    this.processedMessageIds.clear();
  }

  private initializeUnreadCountDebouncer(): void {
    this.unreadCountDebouncer.pipe(debounceTime(1000)).subscribe(() => {
      this.loadUnreadCount();
    });
  }

  private initializeReconnectionHandler(): void {
    this.webSocketService.getConnectionState().subscribe((state) => {
      if (state === 'connected') {
        this.activeConversations.forEach((conversationId) => {
          this.webSocketService.joinConversation(conversationId);
        });
      }
    });
  }

  private initializeWebSocketListeners(): void {
    this.webSocketService.getMessageEvents().subscribe((event) => {
      switch (event.type) {
        case 'new_message':
          this.handleNewMessage(event.data);
          break;
        case 'message_updated':
          this.handleMessageUpdate(event.data);
          break;
        case 'message_deleted':
          this.handleMessageDelete(event.data);
          break;
      }
    });

    this.webSocketService.getConversationEvents().subscribe((event) => {
      switch (event.type) {
        case 'conversation_updated':
          this.handleConversationUpdate(event.data);
          break;
        case 'conversation_deleted':
          this.handleConversationDelete(event.data);
          break;
      }
    });

    this.webSocketService.getOnlineStatusEvents().subscribe((statusInfo) => {
      this.handleOnlineStatusChange(statusInfo);
    });

    this.webSocketService.getUnreadCountEvents().subscribe(() => {
      this.unreadCountDebouncer.next();
    });

    this.webSocketService.getMessageReadEvents().subscribe((readInfo) => {
      this.handleMessageRead(readInfo);
    });
  }

  private handleNewMessage(data: {
    conversationId: string;
    message: Message;
  }): void {
    const { conversationId, message } = data;
    const currentMessages = { ...this.messagesSubject.value };

    if (!currentMessages[conversationId]) {
      currentMessages[conversationId] = [];
    }

    const pendingIndex = currentMessages[conversationId].findIndex(
      (m) => m.isPending && m.tempId && this.isPendingMatch(m, message)
    );

    if (pendingIndex !== -1) {
      currentMessages[conversationId][pendingIndex] = {
        ...message,
        isPending: false,
        sendFailed: false,
      };
    } else if (!this.processedMessageIds.has(message.id)) {
      const exists = currentMessages[conversationId].some(
        (m) => m.id === message.id
      );
      if (!exists) {
        currentMessages[conversationId] = [
          ...currentMessages[conversationId],
          { ...message, isPending: false },
        ];
      }
    }

    this.processedMessageIds.add(message.id);
    this.messagesSubject.next(currentMessages);

    this.updateConversationPreview(conversationId, message);
    this.unreadCountDebouncer.next();
  }

  private isPendingMatch(pending: Message, confirmed: Message): boolean {
    return (
      pending.message_text === confirmed.message_text &&
      pending.sender_id === confirmed.sender_id
    );
  }

  private handleMessageUpdate(data: {
    conversationId: string;
    messageId: string;
    updates: Partial<Message>;
  }): void {
    const { conversationId, messageId, updates } = data;
    const currentMessages = { ...this.messagesSubject.value };

    if (currentMessages[conversationId]) {
      const index = currentMessages[conversationId].findIndex(
        (m) => m.id === messageId
      );
      if (index !== -1) {
        currentMessages[conversationId][index] = {
          ...currentMessages[conversationId][index],
          ...updates,
        };
        this.messagesSubject.next(currentMessages);
      }
    }
  }

  private handleMessageDelete(data: {
    messageId: string;
    conversationId: string;
  }): void {
    const { messageId, conversationId } = data;
    const currentMessages = { ...this.messagesSubject.value };

    if (currentMessages[conversationId]) {
      currentMessages[conversationId] = currentMessages[conversationId].filter(
        (m) => m.id !== messageId
      );
      this.messagesSubject.next(currentMessages);
    }
  }

  private handleConversationUpdate(data: {
    conversationId: string;
    updates: Partial<Conversation>;
  }): void {
    const { conversationId, updates } = data;
    this.updateConversationInList(conversationId, updates);
  }

  private handleConversationDelete(data: { conversationId: string }): void {
    const { conversationId } = data;
    const currentConversations = this.conversationsSubject.value.filter(
      (c) => c.id !== conversationId
    );
    this.conversationsSubject.next(currentConversations);

    const currentMessages = { ...this.messagesSubject.value };
    delete currentMessages[conversationId];
    this.messagesSubject.next(currentMessages);
  }

  private handleOnlineStatusChange(statusInfo: {
    userId: string;
    isOnline: boolean;
  }): void {
    const currentOnlineUsers = new Set(this.onlineUsersSubject.value);

    if (statusInfo.isOnline) {
      currentOnlineUsers.add(statusInfo.userId);
    } else {
      currentOnlineUsers.delete(statusInfo.userId);
    }

    this.onlineUsersSubject.next(currentOnlineUsers);
  }

  private handleMessageRead(readInfo: {
    conversationId: string;
    messageIds: string[];
    readBy: string;
  }): void {
    const currentMessages = { ...this.messagesSubject.value };

    if (currentMessages[readInfo.conversationId]) {
      let updated = false;
      currentMessages[readInfo.conversationId] = currentMessages[
        readInfo.conversationId
      ].map((message) => {
        if (readInfo.messageIds.includes(message.id) && !message.is_read) {
          updated = true;
          return {
            ...message,
            is_read: true,
            read_at: new Date().toISOString(),
          };
        }
        return message;
      });

      if (updated) {
        this.messagesSubject.next(currentMessages);
      }
    }
  }

  private updateConversationPreview(
    conversationId: string,
    message: Message
  ): void {
    this.updateConversationInList(conversationId, {
      last_message_at: message.created_at,
      last_message_preview:
        message.message_text.length > 100
          ? message.message_text.substring(0, 100) + '...'
          : message.message_text,
    });
  }

  private updateConversationInList(
    conversationId: string,
    updates: Partial<Conversation>
  ): void {
    const currentConversations = [...this.conversationsSubject.value];
    const index = currentConversations.findIndex(
      (c) => c.id === conversationId
    );

    if (index !== -1) {
      currentConversations[index] = {
        ...currentConversations[index],
        ...updates,
      };
      currentConversations.sort(
        (a, b) =>
          new Date(b.last_message_at).getTime() -
          new Date(a.last_message_at).getTime()
      );
      this.conversationsSubject.next(currentConversations);
    }
  }

  private loadUnreadCount(): void {
    if (!this.authService.isLoggedIn()) return;

    this.http
      .get<UnreadCountResponse>(`${this.apiUrl}/messages/unread-count`)
      .subscribe({
        next: (response) => this.unreadCountSubject.next(response.unread_count),
        error: () => {},
      });
  }

  sendMessage(
    conversationId: string,
    request: SendMessageRequest
  ): Observable<{ status: string; message: Message }> {
    return new Observable((observer) => {
      const tempId = `temp_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      const currentUser = this.authService.getCurrentUserSync();

      const optimisticMessage: Message = {
        id: tempId,
        conversation_id: conversationId,
        sender_id: currentUser?.id?.toString() || '',
        receiver_id: request.receiverId,
        message_text: request.message,
        is_read: false,
        message_type: 'text',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        sender_name: currentUser
          ? `${currentUser.first_name} ${currentUser.last_name}`
          : 'You',
        sender_first_name: currentUser?.first_name || 'You',
        isPending: true,
        tempId: tempId,
      };

      this.addOptimisticMessage(conversationId, optimisticMessage);

      this.messageQueue.push({
        conversationId,
        request,
        tempId,
        resolve: (value) => observer.next(value),
        reject: (error) => observer.error(error),
      });

      this.processQueue();

      return () => {
        const index = this.messageQueue.findIndex((m) => m.tempId === tempId);
        if (index !== -1) {
          this.messageQueue.splice(index, 1);
        }
      };
    });
  }

  private addOptimisticMessage(conversationId: string, message: Message): void {
    const currentMessages = { ...this.messagesSubject.value };
    if (!currentMessages[conversationId]) {
      currentMessages[conversationId] = [];
    }
    currentMessages[conversationId] = [
      ...currentMessages[conversationId],
      message,
    ];
    this.messagesSubject.next(currentMessages);
  }

  private async processQueue(): Promise<void> {
    if (this.isProcessingQueue || this.messageQueue.length === 0) return;

    this.isProcessingQueue = true;

    while (this.messageQueue.length > 0) {
      const item = this.messageQueue.shift()!;

      try {
        const response = await this.http
          .post<{ status: string; message: Message }>(
            `${this.apiUrl}/messages/conversations/${item.conversationId}/messages`,
            item.request
          )
          .toPromise();

        if (response) {
          this.confirmMessage(
            item.conversationId,
            item.tempId,
            response.message
          );
          item.resolve(response);
        }
      } catch (error: any) {
        this.markMessageFailed(item.conversationId, item.tempId);
        item.reject(
          new Error(error?.error?.message || 'Failed to send message')
        );
      }

      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    this.isProcessingQueue = false;
  }

  private confirmMessage(
    conversationId: string,
    tempId: string,
    confirmedMessage: Message
  ): void {
    const currentMessages = { ...this.messagesSubject.value };

    if (currentMessages[conversationId]) {
      const index = currentMessages[conversationId].findIndex(
        (m) => m.tempId === tempId
      );
      if (index !== -1) {
        currentMessages[conversationId][index] = {
          ...confirmedMessage,
          isPending: false,
          sendFailed: false,
        };
        this.processedMessageIds.add(confirmedMessage.id);
        this.messagesSubject.next(currentMessages);
      }
    }
  }

  private markMessageFailed(conversationId: string, tempId: string): void {
    const currentMessages = { ...this.messagesSubject.value };

    if (currentMessages[conversationId]) {
      const index = currentMessages[conversationId].findIndex(
        (m) => m.tempId === tempId
      );
      if (index !== -1) {
        currentMessages[conversationId][index] = {
          ...currentMessages[conversationId][index],
          isPending: false,
          sendFailed: true,
        };
        this.messagesSubject.next(currentMessages);
      }
    }
  }

  removeFailedMessage(conversationId: string, tempId: string): void {
    const currentMessages = { ...this.messagesSubject.value };

    if (currentMessages[conversationId]) {
      currentMessages[conversationId] = currentMessages[conversationId].filter(
        (m) => m.tempId !== tempId
      );
      this.messagesSubject.next(currentMessages);
    }
  }

  createConversation(
    request: CreateConversationRequest
  ): Observable<ConversationResponse> {
    return this.http
      .post<ConversationResponse>(
        `${this.apiUrl}/messages/conversations`,
        request
      )
      .pipe(
        tap((response) => {
          const currentConversations = this.conversationsSubject.value;
          const exists = currentConversations.some(
            (c) => c.id === response.conversation.id
          );

          if (!exists) {
            this.conversationsSubject.next([
              response.conversation,
              ...currentConversations,
            ]);
          }

          this.joinConversation(response.conversation.id);
        }),
        catchError((error) => {
          const errorMessage =
            error?.error?.message || 'Failed to create conversation';
          return throwError(() => new Error(errorMessage));
        })
      );
  }

  sendFirstMessage(data: {
    listingId: string;
    listingTableName: string;
    sellerId: string;
    message: string;
  }): Observable<{
    status: string;
    conversation: Conversation;
    message: Message;
  }> {
    return this.http
      .post<{ status: string; conversation: Conversation; message: Message }>(
        `${this.apiUrl}/messages/first-message`,
        data
      )
      .pipe(
        tap((response) => {
          const currentConversations = this.conversationsSubject.value;
          const exists = currentConversations.some(
            (c) => c.id === response.conversation.id
          );

          if (!exists) {
            this.conversationsSubject.next([
              response.conversation,
              ...currentConversations,
            ]);
          }

          this.joinConversation(response.conversation.id);
        }),
        catchError((error) => {
          const errorMessage =
            error?.error?.message || 'Failed to send message';
          return throwError(() => new Error(errorMessage));
        })
      );
  }

  getConversationMessages(
    conversationId: string,
    page: number = 1,
    limit: number = 50
  ): Observable<MessagesResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    return this.http
      .get<MessagesResponse>(
        `${this.apiUrl}/messages/conversations/${conversationId}/messages`,
        { params }
      )
      .pipe(
        tap((response) => {
          const currentMessages = { ...this.messagesSubject.value };
          const messagesWithFlags = response.messages.map((msg) => ({
            ...msg,
            isPending: false,
            sendFailed: false,
          }));

          messagesWithFlags.forEach((m) => this.processedMessageIds.add(m.id));

          if (page === 1) {
            const pendingMessages = (
              currentMessages[conversationId] || []
            ).filter((m) => m.isPending);
            currentMessages[conversationId] = [
              ...messagesWithFlags,
              ...pendingMessages,
            ];
          } else {
            const existingMessages = currentMessages[conversationId] || [];
            const newMessages = messagesWithFlags.filter(
              (newMsg) =>
                !existingMessages.some((existing) => existing.id === newMsg.id)
            );
            currentMessages[conversationId] = [
              ...newMessages,
              ...existingMessages,
            ];
          }

          this.messagesSubject.next(currentMessages);
        }),
        catchError((error) => {
          const errorMessage =
            error.status === 404
              ? 'Conversation not found'
              : 'Failed to load messages';
          return throwError(() => new Error(errorMessage));
        })
      );
  }

  getConversations(
    page: number = 1,
    limit: number = 20,
    search?: string
  ): Observable<ConversationsResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (search) {
      params = params.set('search', search);
    }

    return this.http
      .get<ConversationsResponse>(`${this.apiUrl}/messages/conversations`, {
        params,
      })
      .pipe(
        tap((response) => {
          const sortedConversations = response.conversations.sort(
            (a, b) =>
              new Date(b.last_message_at).getTime() -
              new Date(a.last_message_at).getTime()
          );
          this.conversationsSubject.next(sortedConversations);
        }),
        catchError(() =>
          throwError(() => new Error('Failed to load conversations'))
        )
      );
  }

  getUserConversations(
    page: number = 1,
    limit: number = 20,
    search?: string
  ): Observable<ConversationsResponse> {
    return this.getConversations(page, limit, search);
  }

  getConversationDetails(
    conversationId: string
  ): Observable<{ status: string; conversation: Conversation }> {
    return this.http
      .get<{ status: string; conversation: Conversation }>(
        `${this.apiUrl}/messages/conversations/${conversationId}`
      )
      .pipe(
        catchError((error) => {
          const errorMessage =
            error.status === 404
              ? 'Conversation not found'
              : 'Failed to load conversation details';
          return throwError(() => new Error(errorMessage));
        })
      );
  }

  markMessagesAsRead(conversationId: string): Observable<any> {
    return this.http
      .patch(`${this.apiUrl}/messages/conversations/${conversationId}/read`, {})
      .pipe(
        tap(() => {
          this.unreadCountDebouncer.next();

          const currentMessages = this.messagesSubject.value[conversationId];
          if (currentMessages) {
            const unreadMessageIds = currentMessages
              .filter((m) => !m.is_read && !m.isPending)
              .map((m) => m.id);

            if (unreadMessageIds.length > 0) {
              this.webSocketService.markAsRead(
                conversationId,
                unreadMessageIds
              );

              const messages = { ...this.messagesSubject.value };
              messages[conversationId] = messages[conversationId].map((m) =>
                unreadMessageIds.includes(m.id)
                  ? { ...m, is_read: true, read_at: new Date().toISOString() }
                  : m
              );
              this.messagesSubject.next(messages);
            }
          }
        }),
        catchError(() =>
          throwError(() => new Error('Failed to mark messages as read'))
        )
      );
  }

  getUnreadCount(): Observable<UnreadCountResponse> {
    if (!this.authService.isLoggedIn()) {
      return of({ status: 'success', unread_count: 0 });
    }

    return this.http
      .get<UnreadCountResponse>(`${this.apiUrl}/messages/unread-count`)
      .pipe(
        tap((response) => this.unreadCountSubject.next(response.unread_count)),
        catchError(() =>
          throwError(() => new Error('Failed to get unread count'))
        )
      );
  }

  deleteConversation(conversationId: string): Observable<any> {
    return this.http
      .delete(`${this.apiUrl}/messages/conversations/${conversationId}`)
      .pipe(
        tap(() => {
          this.leaveConversation(conversationId);

          const currentConversations = this.conversationsSubject.value.filter(
            (c) => c.id !== conversationId
          );
          this.conversationsSubject.next(currentConversations);

          const currentMessages = { ...this.messagesSubject.value };
          delete currentMessages[conversationId];
          this.messagesSubject.next(currentMessages);

          this.unreadCountDebouncer.next();
        }),
        catchError((error) => {
          const errorMessage =
            error.status === 404
              ? 'Conversation not found'
              : 'Failed to delete conversation';
          return throwError(() => new Error(errorMessage));
        })
      );
  }

  deleteMessage(messageId: string): Observable<any> {
    return this.http
      .delete(`${this.apiUrl}/messages/messages/${messageId}`)
      .pipe(
        catchError((error) => {
          const errorMessage =
            error.status === 404
              ? 'Message not found'
              : 'Failed to delete message';
          return throwError(() => new Error(errorMessage));
        })
      );
  }

  getListingInfo(
    tableName: string,
    listingId: string
  ): Observable<{ status: string; listing: ListingInfo }> {
    return this.http
      .get<{ status: string; listing: ListingInfo }>(
        `${this.apiUrl}/messages/listings/${tableName}/${listingId}`
      )
      .pipe(
        catchError((error) => {
          const errorMessage =
            error.status === 404
              ? 'Listing not found'
              : 'Failed to get listing information';
          return throwError(() => new Error(errorMessage));
        })
      );
  }

  getConversationMessageStream(conversationId: string): Observable<Message[]> {
    return this.messages$.pipe(
      map((allMessages) => allMessages[conversationId] || [])
    );
  }

  joinConversation(conversationId: string): void {
    this.activeConversations.add(conversationId);
    this.webSocketService.joinConversation(conversationId);
  }

  leaveConversation(conversationId: string): void {
    this.activeConversations.delete(conversationId);
    this.webSocketService.leaveConversation(conversationId);
  }

  isUserOnline(userId: string): boolean {
    return this.onlineUsersSubject.value.has(userId);
  }

  getTimestamp(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  }

  formatPrice(price: number, currency: string = 'EUR'): string {
    if (price === 0) return 'Free';

    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(price);
    } catch {
      return `${price} ${currency}`;
    }
  }

  getCurrentUnreadCount(): number {
    return this.unreadCountSubject.value;
  }

  refreshUnreadCount(): void {
    this.unreadCountDebouncer.next();
  }

  isConnected(): boolean {
    return this.webSocketService.isConnected();
  }

  getConnectionState(): Observable<
    'disconnected' | 'connecting' | 'connected'
  > {
    return this.webSocketService.getConnectionState();
  }

  retryConnection(): void {
    this.webSocketService.retry();
  }
}
